import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_DATETIME_FORMAT,
  DEFAULT_DATETIME_FORMAT_24_HRS_FORMAT,
  EMPTY_TIMESTAMP,
  GO_ZERO_DATE,
  TIMEZONE,
} from '../constants/dateTime';

// Extend dayjs with timezone and utc plugins
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Get the timezone display string for the configured timezone
 * @returns string like "(GMT+8)" based on the actual timezone offset
 */
export function getTimezoneDisplay(): string {
  const offset = dayjs().tz(TIMEZONE).utcOffset();
  const hours = Math.floor(Math.abs(offset) / 60);
  const sign = offset >= 0 ? '+' : '-';
  return `(GMT${sign}${hours})`;
}

/**
 * Converts a Date or dayjs object to Unix timestamp in Singapore timezone
 * This ensures consistent timezone handling regardless of user's local timezone
 * @param date The date to format (Date or dayjs object)
 * @returns Unix timestamp
 */
export function formatUnixTimeInSingaporeTimezone(date: Date | Dayjs): number {
  if (!date) return 0;

  // Convert to dayjs if it's a Date object
  const dayjsDate = dayjs.isDayjs(date) ? date : dayjs(date);

  // Interpret the date/time as Singapore timezone
  // This treats the input as if it were entered in Singapore timezone
  const singaporeTime = dayjs.tz(
    dayjsDate.format('YYYY-MM-DD HH:mm:ss'),
    TIMEZONE,
  );

  return Math.floor(singaporeTime.valueOf() / 1000);
}

/**
 * Convert a Unix timestamp that represents Singapore time back to a Date object
 * This is the reverse of formatUnixTimeInSingaporeTimezone
 * @param unixTimestamp Unix timestamp in seconds (UTC time that represents Singapore wall-clock time)
 * @returns Date object representing the same wall-clock time
 */
export function parseUnixTimeFromSingaporeTimezone(
  unixTimestamp: number,
): Date {
  if (!unixTimestamp || unixTimestamp === 0) return new Date(0);

  // Convert Unix timestamp back to Singapore time, then get the wall-clock time
  const singaporeTime = dayjs.unix(unixTimestamp).tz(TIMEZONE);

  // Create a new Date object with the same wall-clock time
  // This ensures the Date object shows the same time the user originally entered
  return dayjs(singaporeTime.format('YYYY-MM-DD HH:mm:ss')).toDate();
}

export function transformDateToUTCString(date: Dayjs) {
  return date.utc().format();
}

export function formatUTCStringToDateString(date: string) {
  if (date === GO_ZERO_DATE) return EMPTY_TIMESTAMP;

  return dayjs(date).tz(TIMEZONE).format(DEFAULT_DATE_FORMAT);
}

export function formatUTCStringToDateTimeString(date: string) {
  if (date === GO_ZERO_DATE) return EMPTY_TIMESTAMP;

  return dayjs(date).tz(TIMEZONE).format(DEFAULT_DATETIME_FORMAT);
}

export function isEndOfDay(date: Dayjs) {
  if (!date) return false;

  return date.hour() === 23 && date.minute() === 59 && date.second() === 59;
}

export function formatDateToStartOfDay(date: Dayjs): Dayjs {
  if (!date) return null;

  const dateClone = dayjs(date);

  return dateClone
    .set('hour', 0)
    .set('minute', 0)
    .set('second', 0)
    .set('millisecond', 0);
}

export const transformDate = (
  timestamp: number,
  is24HrFormat: boolean = false,
): string => {
  return dayjs
    .unix(timestamp)
    .tz(TIMEZONE)
    .format(
      is24HrFormat
        ? DEFAULT_DATETIME_FORMAT_24_HRS_FORMAT
        : DEFAULT_DATETIME_FORMAT,
    );
};
